import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { Badge } from '../ui/badge';
import { 
  Calendar as CalendarIcon, 
  ChevronLeft, 
  ChevronRight, 
  Clock, 
  MapPin, 
  Users,
  RefreshCcw,
  AlertCircle
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { format, addDays, startOfWeek, endOfWeek, addWeeks, isToday, parseISO } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { getCalendarEvents, CalendarEvent } from '../../services/google/calendar';
import { ObjectDrawer } from './ObjectDrawer';

interface CalendarViewProps {
  view: 'day' | 'week';
}

export const CalendarView: React.FC<CalendarViewProps> = ({ view }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'fr' ? fr : enUS;
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // État pour le drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [selectedCalendarId, setSelectedCalendarId] = useState<string>('primary');

  // Calculer la date de début et de fin en fonction de la vue
  const getDateRange = () => {
    if (view === 'day') {
      return {
        start: new Date(currentDate),
        end: new Date(currentDate)
      };
    } else {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 }); // Lundi
      const end = endOfWeek(currentDate, { weekStartsOn: 1 }); // Dimanche
      return { start, end };
    }
  };

  // Charger les événements du calendrier
  const loadEvents = async () => {
    setLoading(true);
    setError(null);
    
    const { start, end } = getDateRange();
    
    try {
      const fetchedEvents = await getCalendarEvents(start, end);
      console.log('Calendar events retrieved:', fetchedEvents);
      setEvents(fetchedEvents);
    } catch (err) {
      console.error('Error fetching calendar events:', err);
      setError(t('calendar.fetchError', 'Impossible de récupérer les événements du calendrier'));
    } finally {
      setLoading(false);
    }
  };

  // Charger les événements au chargement du composant et lors des changements de date/vue
  useEffect(() => {
    loadEvents();
  }, [currentDate, view]);

  // Navigation dans le calendrier
  const navigateCalendar = (direction: 'prev' | 'next') => {
    if (view === 'day') {
      setCurrentDate(prevDate => addDays(prevDate, direction === 'next' ? 1 : -1));
    } else {
      setCurrentDate(prevDate => addWeeks(prevDate, direction === 'next' ? 1 : -1));
    }
  };

  // Format du titre en fonction de la vue
  const getViewTitle = () => {
    if (view === 'day') {
      return format(currentDate, 'PPPP', { locale });
    } else {
      const { start, end } = getDateRange();
      const startFormatted = format(start, 'PPP', { locale });
      const endFormatted = format(end, 'PPP', { locale });
      return `${startFormatted} - ${endFormatted}`;
    }
  };

  // Générer les jours de la semaine pour la vue hebdomadaire
  const renderWeekDays = () => {
    if (view !== 'week') return null;
    
    const days = [];
    const { start } = getDateRange();
    
    for (let i = 0; i < 7; i++) {
      const day = addDays(start, i);
      days.push(day);
    }
    
    return (
      <div className="grid grid-cols-7 gap-2 mb-4">
        {days.map((day, i) => (
          <div key={i} className={`text-center p-2 rounded-md ${isToday(day) ? 'bg-primary/10 font-bold' : ''}`}>
            <div className="text-xs text-muted-foreground">
              {format(day, 'EEEE', { locale })}
            </div>
            <div className={`text-xl ${isToday(day) ? 'text-primary' : ''}`}>{format(day, 'd', { locale })}</div>
          </div>
        ))}
      </div>
    );
  };

  // Filtrer les événements pour le jour actuel (vue quotidienne)
  const getDayEvents = (day?: Date) => {
    const date = day || currentDate;
    
    if (events.length === 0) {
      console.log("No events to filter");
      return [];
    }
    
    console.log(`Filtering events for ${format(date, 'yyyy-MM-dd')}, total events: ${events.length}`);
    
    return events.filter(event => {
      // Vérifier si l'événement a des dates valides
      if (!event.start?.dateTime || !event.end?.dateTime) {
        console.log(`Event missing date information:`, event);
        return false;
      }
      
      try {
        // Vérifier si l'événement est pour le jour spécifié
        const eventStart = parseISO(event.start.dateTime);
        const eventEnd = parseISO(event.end.dateTime);
        
        const startDate = new Date(date);
        startDate.setHours(0, 0, 0, 0);
        
        const endDate = new Date(date);
        endDate.setHours(23, 59, 59, 999);
        
        const isEventInDay = 
          (eventStart >= startDate && eventStart <= endDate) || // Commence ce jour
          (eventEnd >= startDate && eventEnd <= endDate) ||     // Finit ce jour
          (eventStart <= startDate && eventEnd >= endDate);     // Englobe ce jour
        
        if (isEventInDay) {
          console.log(`Event matches day ${format(date, 'yyyy-MM-dd')}:`, event.summary);
        }
        
        return isEventInDay;
      } catch (error) {
        console.error(`Error parsing dates for event:`, error, event);
        return false;
      }
    });
  };

  // Fonction pour ouvrir le drawer avec un événement sélectionné
  const openEventDrawer = (eventId: string, calendarId?: string) => {
    setSelectedEventId(eventId);
    setSelectedCalendarId(calendarId || 'primary'); // Ensure we always have a valid calendarId
    setDrawerOpen(true);
  };

  return (
    <>
      <Card className="w-full">
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <Button variant="outline" size="icon" onClick={() => navigateCalendar('prev')}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <CardTitle className="px-4">{getViewTitle()}</CardTitle>
              <Button variant="outline" size="icon" onClick={() => navigateCalendar('next')}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center space-x-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setCurrentDate(new Date())}
              >
                {t('calendar.today', 'Aujourd\'hui')}
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={loadEvents}
                disabled={loading}
              >
                <RefreshCcw className="h-4 w-4 mr-2" />
                {t('common.refresh', 'Actualiser')}
              </Button>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {error && (
            <div className="mb-4 py-3 px-4 bg-destructive/10 text-destructive rounded-md flex items-center">
              <AlertCircle className="h-4 w-4 mr-2" />
              {error}
            </div>
          )}
          
          {view === 'week' && renderWeekDays()}
          
          {loading ? (
            <div className="space-y-4">
              {Array(3).fill(null).map((_, idx) => (
                <div key={idx} className="flex">
                  <Skeleton className="h-12 w-20 mr-4" />
                  <div className="flex-1">
                    <Skeleton className="h-5 w-1/3 mb-2" />
                    <Skeleton className="h-4 w-1/5" />
                  </div>
                </div>
              ))}
            </div>
          ) : view === 'day' ? (
            // Vue jour
            <div className="space-y-4">
              {getDayEvents().length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CalendarIcon className="mx-auto h-10 w-10 mb-2" />
                  <p>{t('calendar.noEvents', 'Aucun événement pour cette journée')}</p>
                </div>
              ) : (
                getDayEvents().map(event => (
                  <div 
                    key={event.id} 
                    className="flex rounded-md border p-4 hover:bg-muted/40 cursor-pointer"
                    onClick={() => openEventDrawer(event.id, event.calendarId)}
                  >
                    <div className="mr-4 flex flex-col items-center justify-center text-muted-foreground">
                      <span className="text-sm">
                        {format(parseISO(event.start.dateTime), 'HH:mm', { locale })}
                      </span>
                      <span className="text-xs">→</span>
                      <span className="text-sm">
                        {format(parseISO(event.end.dateTime), 'HH:mm', { locale })}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold">{event.summary}</h4>
                      {event.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2">{event.description}</p>
                      )}
                      <div className="mt-2 flex flex-wrap gap-2">
                        {event.location && (
                          <Badge variant="outline" className="flex items-center">
                            <MapPin className="h-3 w-3 mr-1" />
                            <span className="truncate max-w-[200px]">{event.location}</span>
                          </Badge>
                        )}
                        {event.attendees && event.attendees.length > 0 && (
                          <Badge variant="outline" className="flex items-center">
                            <Users className="h-3 w-3 mr-1" />
                            {event.attendees.length} {t('calendar.attendees', 'participant(s)')}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            // Vue semaine
            <div className="grid grid-cols-7 gap-2">
              {Array.from({ length: 7 }).map((_, dayIdx) => {
                const day = addDays(getDateRange().start, dayIdx);
                const dayEvents = getDayEvents(day);
                
                return (
                  <div 
                    key={dayIdx}
                    className={`min-h-[150px] border rounded-md p-2 ${isToday(day) ? 'bg-primary/5 border-primary/20' : ''}`}
                  >
                    <div className="space-y-2">
                      {dayEvents.length === 0 ? (
                        <div className="h-full flex items-center justify-center text-xs text-muted-foreground">
                          {t('calendar.noEventsShort', 'Aucun événement')}
                        </div>
                      ) : (
                        dayEvents.map(event => (
                          <div
                            key={event.id}
                            className="text-xs p-1 rounded-sm bg-primary/10 border border-primary/20 overflow-hidden cursor-pointer hover:brightness-90"
                            style={{ 
                              backgroundColor: event.colorId ? `var(--calendar-color-${event.colorId})` : undefined
                            }}
                            onClick={() => openEventDrawer(event.id, event.calendarId)}
                          >
                            <div className="font-semibold truncate">{event.summary}</div>
                            <div className="flex items-center text-muted-foreground">
                              <Clock className="h-2 w-2 mr-1" />
                              {format(parseISO(event.start.dateTime), 'HH:mm', { locale })}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Drawer pour afficher les détails de l'événement */}
      {selectedEventId && (
        <ObjectDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          objectType="event"
          objectId={selectedEventId}
          calendarId={selectedCalendarId}
        />
      )}
    </>
  );
};
